import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import useDashboardActiveTab from '../helpers/active-tab.hook';
import { DashboardInboxSection } from './section';
import {
  InboxMessageStack,
  InboxMessageStatusStack,
  InboxStoreDefaultListParams,
  MessageStatusType,
  useDashboardInboxStore,
} from './store';
import { functionNoop } from 'src/utils/function/noop';
import { COMMS_PANEL_SLIDE_ANIMATION_TIMEOUT_IN_MS, PlayerChatPanelDesktop } from 'src/player/chat/panel/desktop';
import { usePlayerChatPanelMessageList } from 'src/player/chat/panel/message-list.hook';
import { useHistory } from 'react-router-dom';
import { useDashboardInboxSerializeFilter } from './serialize-filter.hook';
import isEqual from 'lodash/isEqual';
import { useDashboardInboxUserActions } from './user-actions.hook';
import { apiGetCommsInbox } from 'src/utils/journeyApi';
import { useCurrentUser } from 'src/store/user';
import { Button } from 'src/common/button';
import { DoubleChatStrongIcon } from 'src/monet/icons';
import { generateJourneyAliasShareUrlBySlug } from 'src/utils/journey';
import { useCurrentOrganization } from 'src/store/organization';
import { locationGetStringifiedSearchParams } from 'src/utils/location/get-stringified-search-params';
interface Props {
  listMessageType: MessageStatusType;
}

export const DashboardInbox = ({ listMessageType }: Props) => {
  useDashboardActiveTab('inbox');
  const inboxChatStackRef = useRef<InboxMessageStack>();
  const { currentOrganization } = useCurrentOrganization();
  const currentUser = useCurrentUser((state) => state.currentUser);
  const history = useHistory();
  const {
    setMessages,
    getMessageStackByIndex,
    activeFilter,
    listParams,
    updateListParams,
    getIsFilterUpdated,
    activePage,
    updateActiveFilter,
    decrementTotalUnreadMessageCount,
  } = useDashboardInboxStore();
  const { sendChatMessage, listenMessageReadEvent, unlistenMessageReadEvent } = useDashboardInboxUserActions();

  const [pageLoading, setPageLoading] = useState(true);
  const [listLoading, setListLoading] = useState(true);

  const isChatPanelOpenedRef = useRef(false);
  const chatPanelOpenRef = useRef(functionNoop);
  const chatPanelCloseRef = useRef(functionNoop);

  const { serializeFilter, deserializeFilter } = useDashboardInboxSerializeFilter(history.location.search);

  const onCommentSent = async (value: string, callback: () => void) => {
    const sendMessage = () => sendChatMessage(value).then(callback);
    await sendMessage();
  };

  const { renderTitle, connectToChatPanel, renderMessageContentWithSpinner, setActiveStepUUID, setJourneySlug } =
    usePlayerChatPanelMessageList({
      isMobileLayout: false,
      aliasUUID: inboxChatStackRef.current?.status.aliasUUID,
      isPageInbox: true,
      isUserCreator: true,
      organizationSlug: currentOrganization.slug,
      status: inboxChatStackRef.current?.status,
      renderAdditionalTitle: (slug) => (
        <a
          href={generateJourneyAliasShareUrlBySlug(
            currentOrganization,
            slug,
            inboxChatStackRef.current?.status.journeyNodeFriendlyPath,
            { comment: 'last' }
          )}
          target='_blank'
          className='mb-2'
          rel='noopener noreferrer'
        >
          <Button
            icon={<DoubleChatStrongIcon />}
            className='font-serif w-full'
            size='regular'
            variant='secondary'
            label='View comments on Journey'
          />
        </a>
      ),
      currentUser,
      onCommentSend: onCommentSent,
    });

  useEffect(() => {
    if (activeFilter.listMessageType !== listMessageType) {
      updateActiveFilter('listMessageType', listMessageType);
    }
  }, [activeFilter.listMessageType, listMessageType]);

  const replaceUrlSearchParams = () => {
    const isListParamsChanged = !isEqual(InboxStoreDefaultListParams, listParams);
    history.replace({
      pathname: history.location.pathname,
      search: isListParamsChanged ? serializeFilter({ ...listParams, ...activeFilter }) : '',
    });
  };

  useEffect(() => {
    const { search } = window.location;
    if (search) {
      const filterParams = deserializeFilter(search);
      updateListParams(filterParams);
    }
  }, []);

  useEffect(() => {
    return () => {
      unlistenMessageReadEvent();
    };
  }, []);

  useEffect(() => {
    const fetchInboxMessages = () => {
      let queryParamsForPage = '';
      if (activePage !== 1) {
        queryParamsForPage = locationGetStringifiedSearchParams('', { page: activePage });
      }

      // return promiseDelay(800).then(() => MockData.items as any);
      return apiGetCommsInbox(queryParamsForPage);
    };

    const isFilterUpdated = getIsFilterUpdated();
    if (activeFilter.listMessageType || isFilterUpdated) {
      setListLoading(true);

      fetchInboxMessages()
        .then(setMessages)
        .then(replaceUrlSearchParams)
        .then(listenMessageReadEvent)
        .then(() => {
          setPageLoading(false);
          setListLoading(false);
        })
        .catch(console.info);
    }
  }, [activePage, activeFilter.listMessageType, listParams]);

  const onConversationRowClicked = (slug: string, stepUUID: string, rowIndex: number) => {
    let delay = 0;
    if (isChatPanelOpenedRef.current) {
      chatPanelCloseRef.current();
      delay = COMMS_PANEL_SLIDE_ANIMATION_TIMEOUT_IN_MS;
    }
    setTimeout(() => {
      const messageStack = getMessageStackByIndex(rowIndex);
      inboxChatStackRef.current = messageStack;
      setJourneySlug(slug);
      setActiveStepUUID(stepUUID);
      chatPanelOpenRef.current();
    }, delay);
  };

  const onChatPanelClosed = () => {
    isChatPanelOpenedRef.current = false;
  };

  const onChatPanelOpened = async () => {
    if (!isChatPanelOpenedRef.current) {
      await connectToChatPanel(false);
      if (inboxChatStackRef.current?.hasUnreadMessage) {
        decrementTotalUnreadMessageCount();
      }
    }
    isChatPanelOpenedRef.current = true;
  };

  return (
    <>
      <Helmet>
        <title>Journey | Inbox</title>
      </Helmet>
      <div className='flex flex-col w-full'>
        <DashboardInboxSection
          onRowClick={onConversationRowClicked}
          isListLoading={listLoading}
          loading={pageLoading}
        />
      </div>
      <PlayerChatPanelDesktop
        renderTitle={renderTitle}
        panelOpenRef={chatPanelOpenRef}
        onClose={onChatPanelClosed}
        onOpen={onChatPanelOpened}
      >
        {renderMessageContentWithSpinner()}
      </PlayerChatPanelDesktop>
    </>
  );
};
